import { useState } from "react";
import { Plus, Search, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth, useGet } from "@/context/AuthContext";
import { RemovedRecords } from "@/components/app/RecordActions";
import { api, errMsg } from "@/lib/api";
import { FORMS } from "@/lib/forms";
import { downloadCSV } from "@/lib/fmt";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { RecordForm } from "./RecordForm";
import { PageHeader, EmptyState, Loading, ErrorBox } from "./Bits";

export function FormSheet({ open, onOpenChange, formKey, initial, onSaved, title }) {
  const mobile = useIsMobile();
  const form = FORMS[formKey];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={mobile ? "bottom" : "right"} className={`${mobile ? "h-[92vh] rounded-t-2xl" : "sm:max-w-lg"} overflow-y-auto bg-white`} data-testid="form-sheet">
        <SheetHeader className="mb-4 text-left">
          <SheetTitle className="font-display text-2xl">{title || (initial?.id ? form.editLabel || `Edit ${form.title.toLowerCase()}` : form.newLabel || `New ${form.title.toLowerCase()}`)}</SheetTitle>
          <SheetDescription className="sr-only">Fill in the details and save.</SheetDescription>
        </SheetHeader>
        {open && <RecordForm formKey={formKey} initial={initial} onSaved={(d) => { onSaved?.(d); onOpenChange(false); }} testid={`${formKey}-form`} />}
      </SheetContent>
    </Sheet>
  );
}

export default function CrudPage({ formKey, title, eyebrow, subtitle, columns, filters = [], emptyIcon, emptyTitle, emptyText, top,
  addLabel, rowExtra, onRowClick, query = "", canAdd = true, testid, actions }) {
  const form = FORMS[formKey];
  const [filter, setFilter] = useState({});
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const qs = new URLSearchParams({ limit: "200", ...filter, ...(q ? { q } : {}) }).toString();
  const { data, isLoading, error, refetch } = useGet(`${form.path}?${qs}${query}`);
  const items = data?.items || [];
  const saved = () => { refetch(); qc.invalidateQueries({ queryKey: ["/today"] }); };
  const [toDelete, setToDelete] = useState(null);
  const { user } = useAuth();
  const [why, setWhy] = useState("");
  const del = (it) => { setWhy(""); setToDelete(it); };
  const confirmDel = async () => {
    const it = toDelete;
    setToDelete(null);
    try { await api.delete(`${form.path}/${it.id}?reason=${encodeURIComponent(why.trim())}`); toast.success("Archived. Restore it any time from Removed records below."); refetch(); } catch (e) { toast.error(errMsg(e)); }
  };
  const openRow = (it) => (onRowClick ? onRowClick(it) : (setEdit(it), setOpen(true)));

  return (
    <div data-testid={testid || `${formKey}-page`}>
      <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle}>
        {!!items.length && <Button variant="outline" className="h-11" data-testid={`${formKey}-export-csv`} onClick={() => downloadCSV(`${formKey}.csv`, items, columns.map((c) => c.key))}><Download className="h-4 w-4 mr-2" />CSV</Button>}
        {actions}
        {canAdd && <Button className="h-11" data-testid={`${formKey}-add-btn`} onClick={() => { setEdit(null); setOpen(true); }}><Plus className="h-4 w-4 mr-2" />{addLabel || form.newLabel || `New ${form.title.toLowerCase()}`}</Button>}
      </PageHeader>
      {top}
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-3.5 text-stone-400" />
          <input data-testid={`${formKey}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full h-11 rounded-lg border border-input bg-white pl-9 pr-3 text-sm" />
        </div>
        {filters.map((f) => (
          <select key={f.key} data-testid={`${formKey}-filter-${f.key}`} value={filter[f.key] || ""} onChange={(e) => setFilter((s) => { const n = { ...s, [f.key]: e.target.value }; if (!e.target.value) delete n[f.key]; return n; })}
            className="h-11 rounded-lg border border-input bg-white px-3 text-sm">
            <option value="">{`All ${f.label}`}</option>
            {f.options.map((o) => <option key={o}>{o}</option>)}
          </select>
        ))}
      </div>
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} /> : !items.length && (q || Object.keys(filter).length) ? (
        <EmptyState icon={emptyIcon} title={`No ${title.toLowerCase()} match these filters`} text="Try a different search or clear the filters."
          action={<Button variant="outline" data-testid={`${formKey}-clear-filters`} className="h-11 mt-2" onClick={() => { setQ(""); setFilter({}); }}>Clear filters</Button>} />
      ) : !items.length ? (
        <EmptyState icon={emptyIcon} title={emptyTitle || `No ${title.toLowerCase()} yet`} text={emptyText}
          action={canAdd && <Button data-testid={`${formKey}-empty-add`} className="h-11 mt-2" onClick={() => { setEdit(null); setOpen(true); }}>{addLabel || form.newLabel || `Add ${form.title.toLowerCase()}`}</Button>} />
      ) : (
        <>
          <div className="hidden md:block card-surface overflow-x-auto">
            <table className="w-full text-sm" data-testid={`${formKey}-table`}>
              <thead><tr className="border-b bg-[#FBF9F5] text-left">{columns.map((c) => <th key={c.key} className="px-4 py-3 font-medium text-stone-500 text-xs uppercase tracking-wider">{c.label}</th>)}<th /></tr></thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} data-testid={`${formKey}-row-${it.id}`} className="border-b last:border-0 hover:bg-[#FBF9F5] cursor-pointer" onClick={() => openRow(it)}>
                    {columns.map((c) => <td key={c.key} className="px-4 py-3 num">{c.render ? c.render(it) : it[c.key] ?? "—"}</td>)}
                    <td className="px-2 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {rowExtra?.(it, refetch)}
                      <button data-testid={`${formKey}-delete-${it.id}`} onClick={() => del(it)} className="p-2 text-stone-400 hover:text-[#9E2A2B]" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden space-y-2">
            {items.map((it) => (
              <div key={it.id} data-testid={`${formKey}-card-${it.id}`} className="card-surface p-4 flex items-start gap-3" onClick={() => openRow(it)}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-stone-900 truncate">{columns[0].render ? columns[0].render(it) : it[columns[0].key]}</div>
                  <div className="text-xs text-stone-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    {columns.slice(1, 5).map((c) => <span key={c.key}>{c.render ? c.render(it) : it[c.key] ?? "—"}</span>)}
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>{rowExtra?.(it, refetch)}</div>
              </div>
            ))}
          </div>
        </>
      )}
      <FormSheet open={open} onOpenChange={setOpen} formKey={formKey} initial={edit} onSaved={saved} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white" data-testid={`${formKey}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this {form.title.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>It's taken out of every list, report, export and AI answer right away. Your name, the time and your reason are recorded, and it can be restored from Removed records.</AlertDialogDescription>
          </AlertDialogHeader>
          <input className="w-full h-11 rounded-lg border px-3" placeholder="Reason (required)" aria-label="Reason for archiving" value={why} onChange={(e) => setWhy(e.target.value)} data-testid={`${formKey}-delete-reason`} />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${formKey}-delete-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`${formKey}-delete-confirm`} disabled={why.trim().length < 3} onClick={confirmDel} className="bg-[#9E2A2B] hover:bg-[#7f2223]">Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {["owner", "gm", "agm"].includes(user?.role) && <RemovedRecords kind={form.path.replace(/^\//, "")} />}
    </div>
  );
}
